import { Outlet, Link, useLocation, Navigate } from 'react-router';
import { Shield, LayoutDashboard, Building2, UserPlus, LogOut } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { SessionTimeoutModal } from '../auth/SessionTimeoutModal';

const NAV_ITEMS = [
  { href: '/super', icon: LayoutDashboard, label: 'Dashboard' },
  { href: '/super/tenants', icon: Building2, label: 'Tenants' },
  { href: '/super/access-requests', icon: UserPlus, label: 'Access Requests' },
];

export function SuperAdminLayout() {
  const location = useLocation();
  const { user, logout, isSuperAdmin, sessionExpiresAt, extendSession } = useAuth();

  // Only super admins can access this layout
  if (!isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-gray-900 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-gray-800 border-r border-gray-700 flex flex-col">
        {/* Logo */}
        <div className="p-4 border-b border-gray-700">
          <Link to="/super" className="flex items-center space-x-2">
            <Shield className="h-8 w-8 text-red-500" />
            <div>
              <span className="text-lg font-bold text-white">SecureSend</span>
              <span className="ml-1 text-xs bg-red-600 text-white px-1.5 py-0.5 rounded-sm">SUPER</span>
            </div>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4">
          <ul className="space-y-1">
            {NAV_ITEMS.map((item) => {
              const isActive = location.pathname === item.href ||
                (item.href !== '/super' && location.pathname.startsWith(item.href));
              return (
                <li key={item.href}>
                  <Link
                    to={item.href}
                    className={`flex items-center px-3 py-2 rounded-md text-sm font-medium ${
                      isActive
                        ? 'bg-gray-700 text-white'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    }`}
                  >
                    <item.icon className="h-5 w-5 mr-3" />
                    {item.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* User info */}
        <div className="p-4 border-t border-gray-700">
          <div className="flex items-center justify-between">
            <div className="text-sm">
              <p className="text-white font-medium truncate">{user?.name}</p>
              <p className="text-gray-400 text-xs truncate">{user?.email}</p>
            </div>
            <button
              onClick={logout}
              className="p-2 text-gray-400 hover:text-white rounded-md hover:bg-gray-700"
              title="Sign out"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>

      {/* Session timeout warning modal */}
      {sessionExpiresAt && (
        <SessionTimeoutModal
          expiresAt={sessionExpiresAt}
          onExtend={extendSession}
        />
      )}
    </div>
  );
}
